import { DateTime } from 'luxon';
import { IS_LOCAL } from '@/helpers';
import i18n from '@/plugins/i18n';

/**
 * @description 목업 시즌 정보
 */
function mockSeasonWithDraws() {
  return {
    period: {
      start: DateTime.local().startOf('month').toISO(),
      end: DateTime.local().endOf('month').toISO(),
    },
    season: 1,
    draw: 1,
  };
}

/**
 * @description 목업 현재 당첨금 정보
 */
function mockCurrentDrawInfo() {
  const todayAt20 = DateTime.local().set({ hour: 20, minute: 0, second: 0, millisecond: 0 });

  return {
    totalAmount: 90000000,
    remainingTime: todayAt20.toISO(),
  };
}

/**
 * @description 에러 모달 오픈
 */
function openErrModal(errModal, e) {
  console.error(e);

  errModal.open({
    // 오류가 발생했습니다.
    title: e.response?.data?.message?.title ?? i18n.t('etc.modalerr'),
    content: e.response?.data?.message?.content ?? e.response?.data?.message?.replaceAll('|', '<br />') ?? e,
    cancel: false,
  });
}

/**
 * @description 당첨금 세팅
 */
function setWinningAmount(state, winningAmount) {
  const currentWinningAmount = isNaN(parseFloat(winningAmount)) ? 0 : parseFloat(winningAmount);

  state.currentWinningAmount = currentWinningAmount;
}

/**
 * @description 랜덤 숫자 배열 생성
 */
function getShuffledArr(num) {
  const numbers = Array.from({ length: 45 }, (_, i) => i + 1);

  return numbers.sort(() => 0.5 - Math.random()).slice(0, num);
}

/**
 * @description 매일 17시 30분
 */
const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const DAILY_FIVE_PM = DateTime.fromISO('17:00', { zone: 'Asia/Seoul' }).setZone(TIME_ZONE);
const DAILY_FIVE_THIRTY_PM = DateTime.fromISO('17:30', { zone: 'Asia/Seoul' }).setZone(TIME_ZONE);
const DAILY_SIX_PM_FORMAT = DateTime.fromISO('18:00', { zone: 'Asia/Seoul' }).setZone(TIME_ZONE);
const format = {
  ko: {
    five: 'HH시',
    thirty: 'HH시 mm분',
    period: (start, end) => `${start}에서 ${end} 사이`,
    purchase: {
      next: (time) => `익일 ${time}`,
      date: (start, end) => `${start} ~ ${end}`,
    },
  },
  en: {
    five: 'hh:mm',
    thirty: 'hh:mm a',
    period: (start, end) => `${start} - ${end}`,
    purchase: {
      next: (time) => `${time} next day`,
      date: (start, end) => `${start} - ${end}`,
    },
  },
};

export default {
  namespaced: true,
  state() {
    return {
      source: null,
      currentSeason: {},
      currentDraw: 0,
      currentWinningAmount: 0,
      remainingTime: null,
      selectedNums: [],
      numsSelection: new Map(),
      editKey: null,
      userOmc: 0,
      userLuckyTicket: 0,
      megaLottoResultModal: null,
      drawResult: null,
    };
  },
  getters: {
    /**
     * @description 선택된 숫자 개수
     */
    numsLength: (state) => {
      return state.numsSelection.size;
    },
    /**
     * @description 적용 가능한 로또 티켓 개수
     */
    availableLuckyTicket: (state, getters) => {
      if (state.userLuckyTicket < 1) return 0;

      const GENERAL_PRICE = 1;
      const TICKET_PRICE = 0.5;
      const ticketsCount = Math.floor((GENERAL_PRICE * getters.numsLength) / TICKET_PRICE);

      return Math.min(state.userLuckyTicket, ticketsCount);
    },
    /**
     * @description 결제 금액
     */
    purchaseAmount: (_, getters) => {
      return getters.numsLength - getters.availableLuckyTicket / 2;
    },
    /**
     * @description 사용 가능한 userOmc
     */
    availableUserOmc: (state) => {
      return Math.floor(state.userOmc);
    },
    /**
     * @description 유저 구매 가능 여부
     */
    isPurchaseable: (state, getters) => {
      const availableLuckyTicket = Math.floor(state.userLuckyTicket / 2);
      console.log('availableLuckyTicket', availableLuckyTicket);
      console.log('availableUserOmc', getters.availableUserOmc);
      console.log('numsLength', getters.numsLength);

      return getters.numsLength < getters.availableUserOmc + availableLuckyTicket;
    },
    /**
     * @description 매일 17시 30분
     */
    dailyFiveThirtyPM: (_, __, rootState) => {
      return DAILY_FIVE_THIRTY_PM.setLocale(rootState.lang).toFormat(format[rootState.lang].thirty);
    },
    /**
     * @description 17시에서 17시 30분 사이
     */
    dailyPeriod: (_, __, rootState) => {
      const start = DAILY_FIVE_PM.setLocale(rootState.lang).toFormat(format[rootState.lang].five);
      const end = DAILY_FIVE_THIRTY_PM.setLocale(rootState.lang).toFormat(format[rootState.lang].thirty);

      return format[rootState.lang].period(start, end);
    },
    dailyPurchaseNext: (_, __, rootState) => {
      const five = DAILY_FIVE_PM.setLocale(rootState.lang).toFormat(format[rootState.lang].thirty);
      const fiveThirty = DAILY_FIVE_THIRTY_PM.setLocale(rootState.lang).toFormat(format[rootState.lang].thirty);
      const six = DAILY_SIX_PM_FORMAT.setLocale(rootState.lang).toFormat(format[rootState.lang].thirty);
      const nextFive = format[rootState.lang].purchase.next(five);

      return {
        // 18시 ~ 익일 17시
        first: format[rootState.lang].purchase.date(six, nextFive),
        // 17시 ~ 17시 30분
        second: format[rootState.lang].purchase.date(five, fiveThirty),
        // 17시 30분 ~ 18시
        third: format[rootState.lang].purchase.date(fiveThirty, six),
      };
    },
  },
  mutations: {
    /**
     * @description 시즌 정보 세팅
     */
    setSeasonWithDraws(state, { period, season, draw, isSeason }) {
      state.currentSeason = { period, season, isSeason };
      state.currentDraw = draw || 0;
    },
    /**
     * @description 현재 당첨금 정보 세팅
     */
    setCurrentDrawInfo(state, { totalAmount, remainingTime }) {
      setWinningAmount(state, totalAmount);
      state.remainingTime = remainingTime || DateTime.now().toISO();
    },
    /**
     * @description 현재 당첨금 SSE 세팅
     */
    setupWinningAmountSSE(state) {
      if (state.currentDraw === '-') return;

      const eventSource = new EventSource(`${process.env.VUE_APP_TEST_SERVER}/megaluckyball/sse/${state.currentDraw}`, {
        withCredentials: true,
      });

      eventSource.onmessage = (e) => {
        // if (IS_LOCAL) return setWinningAmount(state, state.currentWinningAmount + 1);
        const response = JSON.parse(e.data);
        if (response.data === '-') return state.source.close();
        setWinningAmount(state, response.totalAmount);
      };

      state.source = eventSource;
    },
    /**
     * @description 현재 당첨금 SSE 종료
     */
    clearWinningAmountSSE(state) {
      if (state.source) state.source.close();
      state.source = null;
    },
    /**
     * @description 선택된 숫자 세팅
     */
    setSelectedNums(state, selectedNums) {
      state.selectedNums = [...selectedNums.sort((a, b) => a - b)];
    },
    /**
     * @description 숫자 선택 세팅
     */
    setNumsSelection(state, [selectedNums, type]) {
      const newSelection = new Map(state.numsSelection);
      const key = state.editKey ? state.editKey : `${selectedNums.join('')}${DateTime.local().toMillis()}`;
      newSelection.set(key, { type, nums: selectedNums });

      state.editKey = null;
      state.selectedNums = [];
      state.numsSelection = new Map(newSelection);
    },
    /**
     * @description 숫자 편집 세팅
     */
    setNumsForEdit(state, key) {
      const editNums = state.numsSelection.get(key);

      state.editKey = key;
      state.selectedNums = [...editNums.nums];
    },
    /**
     * @description 숫자 삭제
     */
    deleteNums(state, key) {
      const newSelection = new Map(state.numsSelection);
      newSelection.delete(key);

      state.numsSelection = newSelection;
    },
    /**
     * @description 숫자 선택 초기화
     */
    resetNumsSelection(state) {
      state.numsSelection = new Map();
    },
    /**
     * @description 유저 OMC 세팅
     */
    setUserOmc(state, amount) {
      state.userOmc = amount;
    },
    /**
     * @description 유저 로또 티켓 세팅
     */
    setUserLottoTicket(state, amount) {
      state.userLuckyTicket = amount;
    },
    /**
     * @description 메가 럭키볼 결과 모달 세팅
     */
    setMegaLottoResultModal(state, modal) {
      console.log('setMegaLottoResultModal', modal);
      state.megaLottoResultModal = modal;
    },
    /**
     * @description 메가 럭키볼 결과 세팅
     */
    setDrawResult(state, draw) {
      state.drawResult = draw;
    },
  },
  actions: {
    /**
     * @description API 호출
     */
    async fetchData({ rootState: { vm, globalErrorModal } }, { url, method = 'get', data = null }) {
      try {
        const { data: responseData } = await this._vm.$axios[method](`${process.env.VUE_APP_TEST_SERVER}${url}`, data);
        return responseData;
      } catch (e) {
        if (e?.response?.status === 401) vm.$bvModal.show('login-required');
        else openErrModal(globalErrorModal, e);

        throw e;
      }
    },
    /**
     * @description 시즌 정보 호출
     */
    async fetchSeasonWithDraws({ commit, dispatch }) {
      if (IS_LOCAL) return commit('setSeasonWithDraws', mockSeasonWithDraws());

      const data = await dispatch('fetchData', {
        url: '/megaluckyball/season',
        method: 'get',
      });

      if (data) commit('setSeasonWithDraws', data);
      return;
    },
    /**
     * @description
     */
    async fetchCurrentDrawInfo({ state, commit, dispatch }) {
      if (IS_LOCAL) return commit('setCurrentDrawInfo', mockCurrentDrawInfo());

      const data = await dispatch('fetchData', {
        url: `/megaluckyball/current/${state.currentDraw}`,
        method: 'get',
      });

      if (data) commit('setCurrentDrawInfo', data);
      return;
    },
    async fetchUserAmountAndLuckyTickets({ commit, dispatch }) {
      if (IS_LOCAL) {
        commit('setUserOmc', 2);
        commit('setUserLottoTicket', 3);
        return;
      }

      const [userOmc, userLuckyTicket] = await Promise.all([
        dispatch('fetchData', {
          url: '/user/omc',
          method: 'get',
        }),
        dispatch('fetchData', {
          url: '/user/lottery',
          method: 'get',
        }),
      ]);

      if (userOmc) commit('setUserOmc', userOmc.data);
      if (userLuckyTicket) commit('setUserLottoTicket', userLuckyTicket.data);
      return;
    },
    /**
     * @description 메가 럭키볼 구매
     * @returns {Promise<{draw: number, createDate: string, releaseDate: string, buyNums: number[][]}>}
     */
    async purchaseLottoTicket({ state, commit, dispatch, getters }) {
      if (IS_LOCAL) {
        // await Promise.all([dispatch('fetchCurrentDrawInfo'), dispatch('fetchUserAmountAndLuckyTickets')]);
        commit('resetNumsSelection');
        return {
          draw: 1,
          createDate: '2024-10-23T00:00:00',
          releaseDate: '2024-10-23T00:00:00',
          buyNums: [
            [1, 2, 3, 4],
            [5, 6, 7, 8],
          ],
        };
      }

      const buyNums = Array.from(state.numsSelection.values()).map((item) => item.nums);
      const types = Array.from(state.numsSelection.values()).map((item) => item.type);

      const data = {
        buyNums,
        types,
        price: (getters.numsLength - getters.availableLuckyTicket / 2).toString(),
        turn: state.currentDraw.toString(),
        ticket: getters.availableLuckyTicket,
      };

      /**
       * @type {Promise<{turn: number, createDate: string, releaseDate: string, totalToken: string, omcToken: string, totalAmount: string, lottoTicket: number}>}
       */
      const response = await dispatch('fetchData', {
        url: '/megaluckyball/buy',
        method: 'post',
        data,
      });

      if (response) await Promise.all([dispatch('fetchCurrentDrawInfo'), dispatch('fetchUserAmountAndLuckyTickets')]);
      commit('resetNumsSelection');

      return {
        // 회차
        draw: response.turn,
        // 발행일
        createDate: response.createDate,
        // 추첨일
        releaseDate: response.releaseDate,
        // 유저가 구매한 숫자들
        buyNums,
      };
    },
    setRandomNums({ commit }, num) {
      const shuffled = getShuffledArr(num);

      commit('setSelectedNums', [...shuffled]);
    },
    setAutoRandomNums({ commit }, { num, qty }) {
      const auto = Array.from({ length: qty }, () => [...getShuffledArr(num)]);
      auto.forEach((random) => commit('setNumsSelection', [random, 'auto']));
    },
    handleOpenDrawResultModal({ state, commit }, draw) {
      commit('setDrawResult', draw);
      state.megaLottoResultModal.open();
    },
  },
};
